import React from 'react'
import { useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { Button, ButtonVariant } from '../../../../components/common/Button'
import { TextInput } from '../../../../components/common/TextInput'
import { signUpSchema } from '../../../../forms/schemas/sign_up'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { theme } from '../../../../theme/theme'
import { Checkbox } from '../../../../components/common/Checkbox'
import { createUser } from '../../../../api/users'
import i18n from '../../../../i18n/i18n'
import { TermsOfServiceText } from '../TermsOfServiceText'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../../components/common/Toast/toast-type'
import { FormControl } from '../../../../components/common/FormControl'
import { getLanguage } from '../../../../helpers/i18n'
import { reverseUrl } from '../../../../navigation/reverseUrl'

export const SignUpForm = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()
  const { setToastNotification } = useToastNotifications()
  const navigate = useNavigate()

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<InferType<typeof signUpSchema>>({
    mode: 'all',
    resolver: yupResolver(signUpSchema),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      legalAge: false,
      termsOfService: false,
    },
  })

  const { mutate: mutateCreateUser, isPending: isCreatingUser } = useMutation({
    mutationFn: async (data: InferType<typeof signUpSchema>) => {
      const { name, surname, email } = data

      await createUser({
        name,
        surname,
        email,
        language: getLanguage(i18n.language),
      })
    },
    onSuccess: (_data, variables) => {
      // Nothing to log in with yet, and that is the point: this form no longer
      // takes a password. The link in the mail is where one gets chosen, and it
      // only ever reaches whoever actually owns the address. The API answers
      // the same way whether or not an account came of it, so there is also
      // nothing here to tell the two apart.
      setToastNotification(
        ToastType.SUCCESS,
        translate('authenticationSignUpView.flashMessage.verificationEmailSent')
      )
      // Not the login screen: signing in is exactly what this account can't do
      // yet. The address travels in router state rather than the query, because
      // there is no session to read it from and a url is the one part of a page
      // that gets written down everywhere -- browser history, referrer headers,
      // and the session recordings the error tracker keeps.
      navigate(reverseUrl('verify-email'), {
        state: { email: variables.email.toLowerCase() },
      })
    },
    onError: (error) => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
      captureException(error)
    },
  })

  const onSubmit = async (
    data: InferType<typeof signUpSchema>
  ): Promise<void> => {
    // The address is deliberately not checked up front any more. Doing so let
    // anyone use this form to find out which addresses have a Boappa account.
    // An address that is already taken is handled by mail to its owner.
    mutateCreateUser(data)
  }

  const onError = (): void => {
    if (!isValid) return
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const loading = isCreatingUser

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-4 md:flex-row">
        <Controller
          control={control}
          name="name"
          render={({ field: { value, name, onChange, onBlur } }) => (
            <FormControl
              error={errors.name && getErrorMessage(errors.name)}
              name={name}
              className="grow"
              label={translate('authenticationSignUpView.form.labels.name')}
            >
              <TextInput
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors.name}
              />
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="surname"
          render={({ field: { value, name, onChange, onBlur } }) => (
            <FormControl
              error={errors.surname && getErrorMessage(errors.surname)}
              name={name}
              className="grow"
              label={translate('authenticationSignUpView.form.labels.surname')}
            >
              <TextInput
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors.surname}
              />
            </FormControl>
          )}
        />
      </div>
      <Controller
        control={control}
        name="email"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            error={errors.email && getErrorMessage(errors.email)}
            name={name}
            label={translate('authenticationSignUpView.form.labels.email')}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.email}
              type="email"
            />
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="legalAge"
        render={({ field: { name, value, onChange } }) => (
          <div className="flex gap-2">
            <Checkbox
              name={name}
              value={value}
              onChange={onChange}
              type="checkbox"
            />
            <button
              type="button"
              style={theme.textVariants.caption}
              className="text-left"
              onClick={() => onChange(!value)}
            >
              {translate('authenticationSignUpView.form.checkboxText.legalAge')}
            </button>
          </div>
        )}
      />
      {/* TODO: Do we want to display any error if these are not clicked? Where?  */}
      <Controller
        control={control}
        name="termsOfService"
        render={({ field: { name, value, onChange } }) => (
          <div className="flex gap-2">
            <Checkbox
              name={name}
              value={value}
              onChange={onChange}
              type="checkbox"
            />
            <button
              type="button"
              className="text-left"
              onClick={() => onChange(!value)}
            >
              <TermsOfServiceText />
            </button>
          </div>
        )}
      />
      <div className="mt-4 flex flex-col gap-4 md:mt-12">
        <Button
          type="submit"
          style={{
            ...theme.textVariants.paragraph,
          }}
          className="flex h-14 w-full items-center justify-center"
          label={translate('authenticationSignUpView.signUpButtonTitle')}
          onClick={handleSubmit(onSubmit, onError)}
          loading={loading}
          disabled={loading || !isValid}
          variant={ButtonVariant.PRIMARY}
        />
      </div>
    </form>
  )
}
